import {
  getConceptIdsByGroup,
  getConceptSchemeAfaseLabel,
  isConceptSchemeAfaseId,
} from "constants/TaxonomyConcepts";
import { FaqFilterOption, FaqFilterOptions, FaqFilterState, FaqMeta } from "types/faqFilters";

const ALL_INSURANCE_OPTION: FaqFilterOption = { value: "", name: "Alla händelser" };
const ALL_SUBJECT_OPTION: FaqFilterOption = { value: "", name: "Alla ämnen" };

/** Resolve concept labels for a group from a flat concepts array (FaqMeta shape). */
const getMetaLabelsForGroup = (concepts: string[], group: string): string[] => {
  const groupIds = new Set(getConceptIdsByGroup(group));
  return concepts
    .filter((id) => isConceptSchemeAfaseId(id) && groupIds.has(id))
    .map((id) => getConceptSchemeAfaseLabel(id)!)
    .filter(Boolean);
};

const toSortedOptions = (labels: string[], allOption: FaqFilterOption): FaqFilterOption[] => {
  const uniqueLabels = [...new Set(labels)].sort((a, b) => a.localeCompare(b, "sv"));
  return [allOption, ...uniqueLabels.map((label) => ({ value: label, name: label }))];
};

export const getFaqFilterHeading = (filters: FaqFilterState): string | undefined => {
  const parts = [filters.insuranceEvent, filters.subject].filter(Boolean);
  // Typographic quotes (\u201C = " and \u201D = ") – escaped because the parser
  // rejects these characters directly in source ("Parsing error: Invalid character").
  const quoted = parts.map((p) => "\u201C" + p + "\u201D").join(", ");
  return parts.length ? `Frågor inom ${quoted}` : undefined;
};

export const getFaqMetaFilterOptions = (
  items: ReadonlyArray<FaqMeta>,
  filters?: FaqFilterState,
): FaqFilterOptions => {
  const itemsForInsurance = filters?.subject
    ? items.filter((m) => getMetaLabelsForGroup(m.concepts, "subject").includes(filters.subject!))
    : items;
  const insuranceLabels = itemsForInsurance.flatMap((m) =>
    getMetaLabelsForGroup(m.concepts, "insurance"),
  );
  const insuranceOptions = toSortedOptions(insuranceLabels, ALL_INSURANCE_OPTION);

  const itemsForSubjects = filters?.insuranceEvent
    ? items.filter((m) =>
        getMetaLabelsForGroup(m.concepts, "insurance").includes(filters.insuranceEvent!),
      )
    : items;
  const subjectLabels = itemsForSubjects.flatMap((m) =>
    getMetaLabelsForGroup(m.concepts, "subject"),
  );
  const subjectOptions = toSortedOptions(subjectLabels, ALL_SUBJECT_OPTION);

  return { insuranceOptions, subjectOptions };
};

export const filterFaqMeta = (
  items: ReadonlyArray<FaqMeta>,
  filters: FaqFilterState,
): FaqMeta[] => {
  if (!filters.insuranceEvent && !filters.subject) return [...items];

  return items.filter((m) => {
    if (
      filters.insuranceEvent &&
      !getMetaLabelsForGroup(m.concepts, "insurance").includes(filters.insuranceEvent)
    ) {
      return false;
    }
    if (
      filters.subject &&
      !getMetaLabelsForGroup(m.concepts, "subject").includes(filters.subject)
    ) {
      return false;
    }
    return true;
  });
};
